import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import './contracts.css';
import { BASE_URL } from '../../../../config'; // keep ONLY for file link hrefs
import { apiFetch } from '../../../../apiFetch';

const parseDuration = (durationStr) => {
  const lower = (durationStr || '').toLowerCase().trim();
  const number = parseInt(lower, 10);
  if (Number.isNaN(number)) return 0;

  if (lower.includes('year')) return number * 365 * 24 * 60 * 60 * 1000;
  if (lower.includes('month')) return number * 30 * 24 * 60 * 60 * 1000;
  if (lower.includes('day')) return number * 24 * 60 * 60 * 1000;
  if (lower.includes('hour')) return number * 60 * 60 * 1000;
  if (lower.includes('minute')) return number * 60 * 1000;
  return 0;
};

const formatTime = (ms) => {
  if (ms <= 0) return 'Expired';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds / (60 * 60)) % 24);
  const minutes = Math.floor((totalSeconds / 60) % 60);
  const seconds = totalSeconds % 60;
  return `${days}d ${hours}h ${minutes}m ${seconds}s`;
};

// ✅ Payment helpers
const isPaid = (contract) => {
  return String(contract?.paymentStatus || "").toLowerCase() === "paid";
};

// ✅ Start time = activation time (payment success time)
// Prefer activatedAt. Keep safe fallbacks in case your backend uses a different field name.
const getActivationStartTimeMs = (contract) => {
  const t =
    contract?.activatedAt ||
    contract?.paidAt ||
    contract?.paymentPaidAt ||
    contract?.paymentCompletedAt ||
    contract?.paymentSuccessTime ||
    contract?.activationTime ||
    null;

  if (!t) return null;

  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// ✅ Single source of truth: when is a contract ACTIVE for the player?
// ACTIVE only after payment activation ("Paid") and using activation time as start.
const isContractActive = (contract) => {
  if (!contract) return false;

  const status = String(contract.status || '').toLowerCase();
  const adminApproval = String(contract.adminApproval || '').toLowerCase();

  // Must be signed by the player
  if (!contract.signedByPlayer) return false;

  // Admin must have approved
  if (adminApproval !== 'approved') return false;

  // Must be activated/paid to become ACTIVE
  if (!isPaid(contract)) return false;

  // Explicitly block dead states (include closed)
  if (status === 'terminated' || status === 'rejected' || status === 'closed' || status === 'withdrawn') {
    return false;
  }

  // Contract starts ONLY after activation/payment success
  const startTime = getActivationStartTimeMs(contract);
  if (!startTime) return false;

  // Duration must be valid
  const durationMs = parseDuration(contract.contractDuration);
  if (!durationMs || durationMs <= 0) return false;

  const endTime = startTime + durationMs;

  return Date.now() < endTime;
};

const hasContractExpired = (contract) => {
  if (!contract) return false;

  const adminApproval = String(contract.adminApproval || '').toLowerCase();
  const status = String(contract.status || '').toLowerCase();

  // If it's explicitly closed/terminated/rejected, treat it as not "expired" (it's already ended)
  // Expired is computed only for paid, approved contracts whose time ran out.
  if (status === 'terminated' || status === 'closed' || status === 'rejected' || status === 'withdrawn') return false;

  // Must be admin approved
  if (adminApproval !== 'approved') return false;

  // If not paid, it's not "expired" — it's just not activated yet.
  if (!isPaid(contract)) return false;

  const durationMs = parseDuration(contract.contractDuration);
  if (!durationMs) return false;

  const startTime = getActivationStartTimeMs(contract);
  if (!startTime) return false;

  const endTime = startTime + durationMs;

  return Date.now() >= endTime;
};

const canSubmitFinalReport = (contract) => {
  if (!contract) return false;

  const reportAlreadySubmitted = ['pending_review', 'successful', 'unsuccessful']
    .includes(String(contract.statusAfterContract || '').toLowerCase());

  if (reportAlreadySubmitted) return false;

  const status = String(contract.status || '').toLowerCase();

  return (
    status === 'terminated' ||
    status === 'closed' || // ✅ include closed as a terminal state that can allow report, if you want
    hasContractExpired(contract)
  );
};

const sortContractsForPlayer = (contracts = []) => {
  return [...contracts].sort((a, b) => {
    const aActive = isContractActive(a);
    const bActive = isContractActive(b);

    // 1️⃣ Active contracts first
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;

    // 2️⃣ Most recent first (activation time preferred, then approved time, then created)
    const aTime = new Date(
      a.activatedAt || a.paidAt || a.paymentPaidAt || a.paymentCompletedAt || a.adminApprovalTime || a.createdAt
    ).getTime();

    const bTime = new Date(
      b.activatedAt || b.paidAt || b.paymentPaidAt || b.paymentCompletedAt || b.adminApprovalTime || b.createdAt
    ).getTime();

    return bTime - aTime;
  });
};

const getRoleProfilePath = (role, slug) => {
  if (!role || !slug) return null;

  switch (String(role).toLowerCase()) {
    case "agent":
      return `/agent-profile/${slug}`;
    case "scout":
      return `/scout-profile/${slug}`;
    case "club":
      return `/club-profile/${slug}`;
    default:
      return null;
  }
};

const buildPartnerMeta = (contract) => {
  const role = String(contract?.signedByRole || "").toLowerCase();

  const name =
    contract?.agentName ||
    contract?.scoutName ||
    contract?.clubName ||
    contract?.partnerName ||
    "Unknown";

  // ✅ Try common slug shapes (top-level + nested + partner fallback)
  const agentSlug =
    contract?.agentProfileSlug ||
    contract?.agent?.profileSlug ||
    contract?.partnerSlug ||
    contract?.partner?.profileSlug ||
    null;

  const scoutSlug =
    contract?.scoutProfileSlug ||
    contract?.scout?.profileSlug ||
    contract?.partnerSlug ||
    contract?.partner?.profileSlug ||
    null;

  const clubSlug =
    contract?.clubProfileSlug ||
    contract?.club?.profileSlug ||
    contract?.partnerSlug ||
    contract?.partner?.profileSlug ||
    null;

  let slug = null;
  if (role === "agent") slug = agentSlug;
  if (role === "scout") slug = scoutSlug;
  if (role === "club") slug = clubSlug;

  const profilePath = getRoleProfilePath(role, slug);

  return { role, name, profilePath };
};

const ContractStatusStamp = ({ type, title, subtitle }) => {
  return (
    <div className={`status-stamp ${type}`}>
      <div className="status-stamp-subtitle">{subtitle}</div>
      <div className="status-stamp-title">{title}</div>
    </div>
  );
};

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timers, setTimers] = useState({});
  const [terminationMessage, setTerminationMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [terminationReason, setTerminationReason] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [finalReport, setFinalReport] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [agentRating, setAgentRating] = useState(0);
  const [consentGiven, setConsentGiven] = useState(false);
  const [activeTab, setActiveTab] = useState('contracts');
  const [terminationRequests, setTerminationRequests] = useState([]);

  const playerId = localStorage.getItem('playerId');

  // 🔔 Badge count = pending requests from agent on active contracts
  const pendingTerminationCount = terminationRequests.filter(
    (req) =>
      String(req.status || '').toLowerCase() === 'pending' &&
      ['agent', 'scout', 'club'].includes(
        String(req.requestedBy || '').toLowerCase()
      ) &&
      String(req.ownerRole || '').toLowerCase() === 'player'
  ).length;

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await apiFetch(`/api/contracts/player/${playerId}`);
        if (!res.ok) throw new Error('Failed to fetch contracts');
        const data = await res.json();

        // ✅ Keep all signed contracts EXCEPT rejected/closed (case-insensitive)
        // (This is the same intent you had before; you asked to "include closed" in logic,
        //  not necessarily to display them. If you DO want to display closed, remove 'closed' here.)
        const filteredContracts = (Array.isArray(data) ? data : [])
          .filter((c) => {
            const status = String(c?.status || '').toLowerCase();
            return c?.signedByPlayer && !['rejected', 'closed'].includes(status);
          })
          .sort(
            (a, b) =>
              new Date(
                b.activatedAt || b.paidAt || b.paymentPaidAt || b.paymentCompletedAt || b.adminApprovalTime || b.createdAt
              ).getTime() -
              new Date(
                a.activatedAt || a.paidAt || a.paymentPaidAt || a.paymentCompletedAt || a.adminApprovalTime || a.createdAt
              ).getTime()
          );

        setContracts(filteredContracts);
      } catch (err) {
        console.error('Error fetching contracts:', err);
        setError('Failed to load contracts.');
      } finally {
        setLoading(false);
      }
    };

    if (playerId) {
      fetchContracts();
    }
  }, [playerId]);

  // 🔔 Fetch termination requests owned by the player (agent / scout / club initiated)
  useEffect(() => {
    const fetchTerminationRequests = async () => {
      if (!playerId) {
        console.warn('[TerminationRequests] ❌ No playerId found');
        return;
      }

      console.log('[TerminationRequests] ▶ Fetching for player:', playerId);

      try {
        const res = await apiFetch(
          `/api/termination-requests/owner?ownerId=${encodeURIComponent(playerId)}&ownerRole=player`
        );

        if (!res.ok) throw new Error('Failed to fetch termination requests');
        const data = await res.json();

        console.log('[TerminationRequests] 📦 Raw response:', data);

        if (!Array.isArray(data)) {
          console.error('[TerminationRequests] ❌ Expected array but got:', data);
          setTerminationRequests([]);
          return;
        }

        const filtered = data.filter((req) => {
          const requestedBy = String(req.requestedBy || '').toLowerCase();
          const ownerRole = String(req.ownerRole || '').toLowerCase();
          const status = String(req.status || '').toLowerCase();

          const include =
            ['agent', 'scout', 'club'].includes(requestedBy) &&
            ownerRole === 'player' &&
            status === 'pending';

          console.log('[TerminationRequests] 🔍 Check request', {
            id: req.id,
            requestedBy,
            ownerRole,
            status,
            include,
          });

          return include;
        });

        console.log('[TerminationRequests] ✅ Final filtered requests:', filtered);

        setTerminationRequests(filtered);

      } catch (err) {
        console.error('[TerminationRequests] ❌ Fetch failed:', err.message);
      }
    };

    fetchTerminationRequests();
  }, [playerId]);

  // Countdown timers for contracts
  useEffect(() => {
    if (contracts.length === 0) return;

    const interval = setInterval(() => {
      const updatedTimers = {};

      contracts.forEach((contract) => {
        const status = String(contract?.status || '').toLowerCase();
        const adminApproval = String(contract?.adminApproval || '').toLowerCase();

        // 0️⃣ Explicit CLOSED (show closed label if it somehow appears in list)
        if (status === 'closed') {
          updatedTimers[contract.id] = 'CLOSED';
          return;
        }

        // 1️⃣ Pending admin approval (player signed, admin not yet approved)
        if (contract.signedByPlayer && adminApproval !== 'approved') {
          updatedTimers[contract.id] = 'PENDING';
          return;
        }

        // 2️⃣ Admin approved but NOT paid yet -> Awaiting Activation (no countdown)
        if (
          contract.signedByPlayer &&
          adminApproval === 'approved' &&
          !isPaid(contract) &&
          status !== 'terminated' &&
          status !== 'rejected' &&
          status !== 'closed'
        ) {
          updatedTimers[contract.id] = 'AWAITING_ACTIVATION';
          return;
        }

        // 3️⃣ Terminated
        if (status === 'terminated') {
          updatedTimers[contract.id] = 'TERMINATED';
          return;
        }

        // 4️⃣ Expired (only if it was paid+approved and time ran out)
        if (hasContractExpired(contract)) {
          updatedTimers[contract.id] = 'EXPIRED';
          return;
        }

        // ✅ Active → show countdown ONLY if contract is active (paid + not expired)
        if (isContractActive(contract)) {
          const duration = parseDuration(contract.contractDuration);

          if (!duration) {
            updatedTimers[contract.id] = 'EXPIRED';
            return;
          }

          const startTime = getActivationStartTimeMs(contract);
          if (!startTime) {
            updatedTimers[contract.id] = 'AWAITING_ACTIVATION';
            return;
          }

          const endTime = startTime + duration;
          const timeLeft = endTime - Date.now();

          updatedTimers[contract.id] = timeLeft > 0 ? formatTime(timeLeft) : 'EXPIRED';
          return;
        }
      });

      setTimers(updatedTimers);
    }, 1000);

    return () => clearInterval(interval);
  }, [contracts]);

  const getStatusColor = (adminApproval) => {
    const val = String(adminApproval || '').toLowerCase();
    if (val === 'approved') return 'lime';
    if (val === 'rejected') return 'red';
    if (val === 'closed') return 'gray';
    return 'orange'; // NULL = pending
  };

  const handleTerminationRequest = async (contract) => {
    if (!isContractActive(contract)) {
      setTerminationMessage(
        '⚠️ You can only request termination while the contract is active.'
      );
      setTimeout(() => setTerminationMessage(''), 5000);
      return;
    }

    const duration = parseDuration(contract.contractDuration);
    const quarterDuration = duration * 0.25;
    const now = Date.now();

    // ✅ Cooldown timing should be based on activation start time (not admin approval)
    const activatedTime = getActivationStartTimeMs(contract);
    const startTimeMs = activatedTime ?? new Date(contract.adminApprovalTime || contract.createdAt).getTime();
    const approvedTime = startTimeMs;

    try {
      const res = await apiFetch(
        `/api/check-termination-request?contractId=${encodeURIComponent(contract.id)}&playerId=${encodeURIComponent(playerId)}`
      );

      if (!res.ok) throw new Error('Error checking termination status');
      const { exists, lastRequest } = await res.json();

      // ❌ Block if PLAYER already has a pending request
      if (
        exists &&
        String(lastRequest?.status || '').toLowerCase() === 'pending' &&
        String(lastRequest?.requestedBy || '').toLowerCase() === 'player'
      ) {
        setTerminationMessage(
          '⚠️ You already submitted a termination request for this contract.'
        );
        setTimeout(() => setTerminationMessage(''), 5000);
        return;
      }

      // ✅ Only apply cooldown if PLAYER'S last request was rejected
      if (
        String(lastRequest?.status || '').toLowerCase() === 'rejected' &&
        String(lastRequest?.requestedBy || '').toLowerCase() === 'player'
      ) {
        const rejectionTime = new Date(lastRequest.createdAt).getTime();
        const nextAllowedTime = rejectionTime + quarterDuration;

        if (now < nextAllowedTime) {
          const timeLeft = nextAllowedTime - now;
          const readable = formatTime(timeLeft);
          setTerminationMessage(
            `🕒 Your last request was rejected. You can request again in: ${readable}`
          );
          setTimeout(() => setTerminationMessage(''), 5000);
          return;
        }
      }

      // ⏳ First-time request: must wait 25% of contract duration (from activation time)
      if (now >= approvedTime + quarterDuration) {
        setSelectedContract(contract);
        setShowModal(true);
      } else {
        const timeLeft = approvedTime + quarterDuration - now;
        const readable = formatTime(timeLeft);
        setTerminationMessage(
          `⏳ You can request termination in: ${readable}`
        );
        setTimeout(() => setTerminationMessage(''), 5000);
      }
    } catch (err) {
      console.error('Error checking termination status:', err);
      setTerminationMessage('❌ Error checking request status.');
      setTimeout(() => setTerminationMessage(''), 5000);
    }
  };

  const handleFinalReportSubmit = async () => {
    if (!selectedContract?.id) {
      alert('No contract selected.');
      return;
    }

    if (!finalReport.trim()) {
      alert('Please write something in your report.');
      return;
    }

    if (!agentRating) {
      alert('Please rate your experience with the agent.');
      return;
    }

    if (!consentGiven) {
      alert('You must give consent to submit the report.');
      return;
    }

    const formData = new FormData();
    formData.append('contractId', selectedContract.id);
    formData.append('playerId', playerId);
    formData.append('reportText', finalReport);
    formData.append('agentRating', agentRating);
    formData.append('consentGiven', consentGiven);

    selectedPhotos.forEach((photo) => {
      formData.append('photos', photo);
    });

    try {
      const res = await apiFetch(`/api/submit-final-report`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to submit final report.');

      alert('Your final report was submitted successfully.');

      setShowReportModal(false);
      setSelectedContract(null);
      setFinalReport('');
      setSelectedPhotos([]);
      setAgentRating(0);
      setConsentGiven(false);

    } catch (err) {
      console.error(err);
      alert('Failed to submit final report.');
    }
  };

  const handleTerminationDecision = async (requestId, decision) => {
    try {
      const res = await apiFetch(`/api/termination-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId,
          decision,
          deciderId: Number(playerId),
          deciderRole: 'player',
        }),
      });
      if (!res.ok) throw new Error('Failed to update termination request.');

      alert(`Termination request ${decision}.`);

      const updatedRes = await apiFetch(
        `/api/termination-requests/owner?ownerId=${encodeURIComponent(playerId)}&ownerRole=player`
      );

      if (!updatedRes.ok) throw new Error('Failed to refresh termination requests');
      const updated = await updatedRes.json();
      setTerminationRequests(updated);

    } catch (err) {
      console.error('Error updating termination request:', err);
      const message = err.message;
      alert(message || 'Failed to update request.');
    }
  };

  const handleModalSubmit = async () => {
    if (!selectedContract?.id) {
      alert("No contract selected.");
      return;
    }

    if (!terminationReason.trim()) {
      alert("Please enter a reason for termination.");
      return;
    }

    try {
      const res = await apiFetch(`/api/request-contract-termination`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractId: selectedContract.id,
          playerId,
          reason: terminationReason,
          requestedBy: "player",
          ownerId: playerId,
          ownerRole: "player",
        }),
      });

      if (!res.ok) throw new Error("Failed to submit termination request.");

      alert("Your termination request was submitted successfully.");
      setShowModal(false);
      setSelectedContract(null);
      setTerminationReason("");

    } catch (err) {
      alert(err.message || "Failed to submit termination request.");
    }
  };

  return (
    <div className="contracts-container">
      <div className="contracts-header">
        <h2 className="contracts-title">Manage Contracts</h2>
        <p className="contract-subtitle">
          These are the official contracts you have accepted from Agents, Scouts, and Clubs.
        </p>

        <div className="contract-tabs">
          <button
            className={activeTab === 'contracts' ? 'active-tab' : ''}
            onClick={() => setActiveTab('contracts')}
          >
            Contracts
          </button>

          <button
            className={activeTab === 'termination' ? 'active-tab' : ''}
            onClick={() => setActiveTab('termination')}
            style={{ position: 'relative' }}
          >
            Termination Requests
            {pendingTerminationCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-10px',
                  backgroundColor: 'red',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '2px 6px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  lineHeight: 1,
                }}
              >
                {pendingTerminationCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {loading && <p>Loading contracts...</p>}
      {error && <p className="contract-error">{error}</p>}

      {!loading && contracts.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '40px' }}>
          No signed contracts found.
        </p>
      )}

      {activeTab === 'contracts' &&
        sortContractsForPlayer(contracts).map((contract) => {
          const relatedRequest = terminationRequests.find(
            (req) =>
              req.contractId === contract.id &&
              ["agent", "scout", "club"].includes(String(req.requestedBy || "").toLowerCase())
          );

          const active = isContractActive(contract);

          const { role: partnerRole, name: partnerName, profilePath } =
            buildPartnerMeta(contract);
          const roleLabel = partnerRole
            ? partnerRole.charAt(0).toUpperCase() + partnerRole.slice(1)
            : 'Agent';

          const status = String(contract.status || '').toLowerCase();
          const adminApproval = String(contract.adminApproval || '').toLowerCase();

          return (
            <div
              key={contract.id}
              className="contract-card neon-device"
              style={{ position: 'relative' }}
            >
              <span className="neon-corner tl" />
              <span className="neon-corner tr" />
              <span className="neon-corner bl" />
              <span className="neon-corner br" />

              <div className="neon-screen">
                <div className="neon-title-row">
                  <div className="neon-line" />
                  <h3 className="contract-card-title neon-title">CONTRACT</h3>
                  <div className="neon-line" />
                </div>

                <div className="contract-card-body">
                  <div className="contract-left">
                    <p className="contract-row">
                      <span className="contract-label">Player Name:</span>
                      <span className="contract-value">{contract.playerFullName}</span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Username:</span>
                      <span className="contract-value">{contract.playerUsername}</span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Contract Duration:</span>
                      <span className="contract-value">{contract.contractDuration}</span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Uploaded Contract:</span>
                      <span className="contract-value">
                        {contract.agentContractUrl ? (
                          <a
                            href={`${BASE_URL}${contract.agentContractUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contract-link"
                          >
                            📄 View Uploaded Contract
                          </a>
                        ) : (
                          "Not uploaded"
                        )}
                      </span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Contract With:</span>
                      <span className="contract-value">
                        {profilePath ? (
                          <Link to={profilePath} className="contract-link">
                            View {roleLabel} Profile ({partnerName})
                          </Link>
                        ) : (
                          `${partnerName} (${roleLabel})`
                        )}
                      </span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Status:</span>
                      <span className="contract-value">
                        {status === "terminated" ? (
                          <span style={{ color: "red", fontWeight: "bold" }}>❌ Terminated</span>
                        ) : status === "closed" ? (
                          <span style={{ color: "gray", fontWeight: "bold" }}>⛔ Closed</span>
                        ) : (
                          <span className="contract-status">Accepted by Player ✅</span>
                        )}
                      </span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Admin Approval:</span>
                      <span
                        className="contract-value"
                        style={{ color: getStatusColor(contract.adminApproval) }}
                      >
                        {adminApproval === "approved"
                          ? "approved ✅"
                          : contract.adminApproval || "Pending"}
                      </span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Activation / Payment:</span>
                      <span
                        className="contract-value"
                        style={{ color: isPaid(contract) ? "lime" : "orange" }}
                      >
                        {isPaid(contract) ? "Paid ✅" : contract.paymentStatus || "Unpaid"}
                      </span>
                    </p>

                    <p className="contract-row">
                      <span className="contract-label">Signed On:</span>
                      <span className="contract-value">
                        {new Date(
                          contract.updatedAt || contract.createdAt
                        ).toLocaleDateString()}
                      </span>
                    </p>

                    {relatedRequest &&
                      String(relatedRequest.status || "").toLowerCase() !== "rejected" && (
                        <div className="termination-request" style={{ marginTop: "10px" }}>
                          <p className="contract-row">
                            <span className="contract-label">Termination Reason:</span>
                            <span className="contract-value">{relatedRequest.reason}</span>
                          </p>

                          <p className="contract-row">
                            <span className="contract-label">Status:</span>
                            <span className="contract-value">{relatedRequest.status}</span>
                          </p>

                          <p className="contract-row">
                            <span className="contract-label">Requested At:</span>
                            <span className="contract-value">
                              {new Date(relatedRequest.createdAt).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      )}
                  </div>

                  <div className="contract-timer-section neon-right-panel">
                    {(status === 'terminated' || status === 'closed' || hasContractExpired(contract)) ? (
                      <div className="terminated-stamp-area">
                        {status === 'terminated' ? (
                          <img
                            src="/terminated-stamp.png"
                            alt="Terminated"
                            className="terminated-stamp"
                          />
                        ) : status === 'closed' ? (
                          <div className="contract-countdown-timer expired">
                            Closed
                          </div>
                        ) : (
                          <div className="contract-countdown-timer expired">
                            Expired
                          </div>
                        )}

                        {canSubmitFinalReport(contract) && (
                          <button
                            className="submit-report-button neon-btn"
                            onClick={() => {
                              setSelectedContract(contract);
                              setShowReportModal(true);
                            }}
                          >
                            Submit Final Report
                          </button>
                        )}
                      </div>
                    ) : adminApproval !== 'approved' ? (
                      <ContractStatusStamp
                        type="pending"
                        title="Pending Approval"
                        subtitle="Admin review in progress"
                      />
                    ) : adminApproval === 'approved' && !isPaid(contract) ? (
                      <ContractStatusStamp
                        type="pending"
                        title="Awaiting Activation"
                        subtitle="Partner must complete payment to start countdown"
                      />
                    ) : active ? (
                      <>
                        <p className="contract-countdown-message">
                          You are currently under contract which expires in:
                        </p>

                        <div className="contract-countdown-timer">
                          {timers[contract.id]}
                        </div>

                        <button
                          className="terminate-button neon-btn danger"
                          onClick={() => handleTerminationRequest(contract)}
                        >
                          Request Termination
                        </button>

                        {terminationMessage && (
                          <p className="termination-message">
                            {terminationMessage}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="contract-countdown-timer expired">
                        Expired
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

      {activeTab === 'termination' && terminationRequests.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '40px' }}>
          No termination requests submitted.
        </p>
      )}

      {activeTab === 'termination' &&
        terminationRequests
          .filter((req) =>
            ["agent", "scout", "club"].includes(String(req.requestedBy || "").toLowerCase())
          )
          .map((req) => {
            const relatedContract = contracts.find((c) => c.id === req.contractId);
            const active = relatedContract && isContractActive(relatedContract);

            return (
              <div key={req.id} className="termination-request-card neon-device" style={{ position: "relative" }}>
                <span className="neon-corner tl" />
                <span className="neon-corner tr" />
                <span className="neon-corner bl" />
                <span className="neon-corner br" />

                <div className="neon-screen">
                  <div className="neon-title-row">
                    <div className="neon-line" />
                    <h3 style={{ color: 'yellow' }} className="neon-title">TERMINATION REQUEST</h3>
                    <div className="neon-line" />
                  </div>

                  <p className="contract-row">
                    <span className="contract-label">Contract ID:</span>
                    <span className="contract-value">{req.contractId}</span>
                  </p>

                  <p className="contract-row">
                    <span className="contract-label">Reason:</span>
                    <span className="contract-value">{req.reason}</span>
                  </p>

                  <p className="contract-row">
                    <span className="contract-label">Requested By:</span>
                    <span className="contract-value">{req.requestedBy}</span>
                  </p>

                  <p className="contract-row">
                    <span className="contract-label">Status:</span>
                    <span className="contract-value">{req.status}</span>
                  </p>

                  <p className="contract-row">
                    <span className="contract-label">Requested At:</span>
                    <span className="contract-value">
                      {new Date(req.createdAt).toLocaleString()}
                    </span>
                  </p>

                  {String(req.status || '').toLowerCase() === 'pending' && active ? (
                    <div className="termination-actions">
                      <button
                        className="approve-btn neon-btn"
                        onClick={() => handleTerminationDecision(req.id, 'approved')}
                      >
                        Approve
                      </button>
                      <button
                        className="reject-btn neon-btn danger"
                        onClick={() => handleTerminationDecision(req.id, 'rejected')}
                      >
                        Reject
                      </button>
                    </div>
                  ) : String(req.status || '').toLowerCase() === 'pending' && !active ? (
                    <p style={{ color: 'orange', fontWeight: 'bold', marginTop: '10px' }}>
                      ⏳ Contract has expired — no action available
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-content neon-modal">
            <h3>Request the Agent/Scout/Club to Terminate Contract</h3>
            <textarea
              placeholder="Enter your reason for termination..."
              value={terminationReason}
              onChange={(e) => setTerminationReason(e.target.value)}
            />
            <div className="modal-buttons">
              <button className="submit-btn neon-btn" onClick={handleModalSubmit}>
                Submit Request
              </button>
              <button
                className="cancel-btn neon-btn"
                onClick={() => {
                  setShowModal(false);
                  setSelectedContract(null);
                  setTerminationReason('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="modal-backdrop">
          <div className="modal-content neon-modal">
            <h3>Submit Final Report</h3>

            <textarea
              placeholder="Give the full detailed report of your contract. Describe the outcome of the contract. Did the agent help you get signed a club? What happened?"
              value={finalReport}
              onChange={(e) => setFinalReport(e.target.value)}
            />

            <div style={{ marginTop: '10px', color: 'yellow' }}>
              Rate Your Experience with the Contract Partner:
              <div>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    onClick={() => setAgentRating(star)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '24px',
                      color: star <= agentRating ? 'gold' : 'gray',
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '10px' }}>
              <label style={{ color: 'yellow' }}>
                Upload Photos (optional):
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setSelectedPhotos([...e.target.files])}
              />
            </div>

            <div style={{ marginTop: '10px', color: 'white' }}>
              <label>
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                I consent to my report being published as a testimonial.
              </label>
            </div>

            <div className="modal-buttons">
              <button className="submit-btn neon-btn" onClick={handleFinalReportSubmit}>
                Submit Report
              </button>
              <button
                className="cancel-btn neon-btn"
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedContract(null);
                  setFinalReport('');
                  setSelectedPhotos([]);
                  setAgentRating(0);
                  setConsentGiven(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;


































